import * as React from "react";
import { connect } from "react-redux";
import { useParams, useHistory } from "react-router-dom";
import moment from "moment";
import { v4 as uuidv4 } from "uuid";

import { parseApiDate } from "../../helpers/date";

import {
  updateWorkout,
  UpdateWorkout,
  fetchUser,
  FetchUser
} from "../../resources/user/user.actions";
import Header from "../../layouts/main/header";
import { exercisesConfig } from "../../constants/exercises";
import SelectLifts from "../../components/select-lifts";
import EditButton from "../../components/save-button";
import { getUser } from "../../resources/user/user.selectors";
import { IStore } from "../../resources/types";
import { IUser, IWorkout, IExercise } from "../../resources/user/types";

import styles from "./exercises.module.css";

interface IProps {
  user: IUser | undefined;
  fetchUser: FetchUser;
  updateWorkout: UpdateWorkout;
}

function Exercises({ user, fetchUser, updateWorkout }: IProps) {
  const history = useHistory();
  const { id, workoutId } = useParams<{ id: string; workoutId: string }>();
  const [selectedLifts, setSelectedLifts] = React.useState<string[]>([]);
  const [workout, setWorkout] = React.useState<IWorkout>();
  const [isEditingDate, setIsEditingDate] = React.useState(false);
  const [showSaved, setShowSaved] = React.useState(false);

  const handleSelectedLifts = (selected: string[]) =>
    setSelectedLifts(selected);

  React.useEffect(() => {
    fetchUser(id);
  }, [fetchUser, id]);

  React.useEffect(() => {
    if (user && user.logbook) {
      const w = user.logbook.find(we => we.id === workoutId);
      const exercisesIds = ((w && w.exercises) || []).map(e => e.id);
      setWorkout(w);
      setSelectedLifts(exercisesIds);
    }
  }, [user, workoutId]);

  const onDateChange = async (newDate: string) => {
    if (!workout) return;

    if (newDate === moment(workout.date).format("YYYY-MM-DD")) {
      setIsEditingDate(false);
      return;
    }

    try {
      await updateWorkout(id, workoutId, {
        date: moment(newDate).format(),
        timestamp: moment.utc(newDate).unix()
      });

      setWorkout({
        ...workout,
        date: moment(newDate).format(),
        timestamp: moment.utc(newDate).unix()
      });

      setShowSaved(true);
      setTimeout(() => setShowSaved(false), 1500);
    } finally {
      setIsEditingDate(false);
    }
  };

  if (!workout || !user) {
    return null;
  }

  const onClickSave = async () => {
    const exercises = selectedLifts.map(lift => {
      const existing = (workout.exercises || []).find(
        exercise => exercise.id === lift
      );
      if (existing) {
        return existing;
      }

      const newExercise: any = exercisesConfig.find(
        config => config.id === lift
      );

      return {
        ...newExercise,
        exerciseSets: [
          {
            id: uuidv4(),
            sets: 3,
            reps: 5,
            weight: 45,
            pos: 0
          }
        ]
      } as IExercise;
    });

    await updateWorkout(id, workoutId, { exercises });

    history.goBack();
  };

  const TitleComponent = () => (
    <div className={styles.headerContainer}>
      <div
        className={styles.headerTitle}
        // eslint-disable-next-line react/prop-types
      >{`Edit ${user.firstName}'s workout`}</div>
      <div className={styles.subTitle}>
        {isEditingDate ? (
          <input
            type="date"
            className={styles.dateInput}
            defaultValue={parseApiDate(workout.date).format("YYYY-MM-DD")}
            onChange={e => onDateChange(e.target.value)}
            onBlur={() => setIsEditingDate(false)}
            autoFocus // eslint-disable-line jsx-a11y/no-autofocus
          />
        ) : (
          <span
            className={styles.dateDisplay}
            onClick={() => setIsEditingDate(true)}
            onKeyDown={e =>
              (e.key === "Enter" || e.key === " ") && setIsEditingDate(true)
            }
            role="button"
            tabIndex={0}
          >
            {parseApiDate(workout.date).format("ddd, MMM D, YYYY")}
            {showSaved ? (
              <span className={styles.checkmark}>&#10003;</span>
            ) : (
              <span className={styles.pencil}>&#9998;</span>
            )}
          </span>
        )}
      </div>
    </div>
  );
  const RightButton = () => (
    <EditButton className={styles.saveExercises} onClick={onClickSave}>
      save
    </EditButton>
  );

  return (
    <>
      <Header
        className={styles.header}
        TitleComponent={TitleComponent}
        RightButton={RightButton}
      />
      <div className={styles.title}>Select your lifts.</div>
      <SelectLifts
        initialValues={selectedLifts}
        onChange={handleSelectedLifts}
      />
    </>
  );
}
export default connect(
  (state: IStore) => ({
    user: getUser(state)
  }),
  {
    fetchUser,
    updateWorkout
  }
)(Exercises);
